import type { ChangeEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addStorageObject,
  copyObjectAction,
  editStorageObject,
} from "../../../../app/state/storageObjectSlice/actions/actions";
import { useAppDispatch } from "../../../../services/redux/hooks/use-dispatch";
import { StorageScope } from "../../../../services/api/instances/storageApi/types/service_types";
import { useStorageSelectors } from "../../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { useTranslation } from "react-i18next";
import { TAppModal, TModalsIds } from "../../../../app/state/modalSlice/types";
import { createNotification } from "../../../../app/state/notificationSlice/slice";

export const useObjectManager = (
  service: StorageScope,
  defaultName: string | undefined,
  objectManager: TAppModal,
  closeModal: (id: TModalsIds, subtype?: string) => void,
  addFolder?: (name: string) => void,
) => {
  const dispatch = useAppDispatch();
  const { objects } = useStorageSelectors(service);
  const [name, setName] = useState<string>("");
  const { t } = useTranslation();

  const title = useMemo(
    () =>
      objectManager.subtype === "create_folder_object"
        ? t("common.create_folder_title")
        : objectManager.subtype === "create_copy_object"
          ? t("common.copy_object_title")
          : objectManager.subtype === "create_object"
            ? t("common.create_object_title")
            : t("name_change_modal.title"),
    [objectManager.subtype],
  );

  const onOkButtonTitle = useMemo(
    () =>
      objectManager.subtype === "create_object" ||
      objectManager.subtype === "create_copy_object" ||
      objectManager.subtype === "create_folder_object"
        ? t("common.confirm_button")
        : t("name_change_modal.change_button"),
    [objectManager.subtype],
  );

  useEffect(() => {
    if (objectManager.isOpen) {
      if (objectManager.subtype === "create_folder_object") {
        setName(defaultName ? defaultName : t("common.default_folder_name"));
      } else if (objectManager.subtype === "edit_object") {
        setName(objectManager.info?.value ?? "");
      } else if (objectManager.subtype === "create_copy_object") {
        setName(
          `${t("common.copy_object_prefix")} ${objectManager.info?.value}`,
        );
      }
    } else {
      setName("");
    }
  }, [
    objectManager.isOpen,
    objectManager.subtype,
    defaultName,
    objectManager.info?.value,
  ]);

  const handleChangeFolderName = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setName(event.target.value);
    },
    [],
  );

  const handleUpdateObjectWithNewName = useCallback(() => {
    if (!objectManager?.info?.id || name === "") return;
    const targetObject = objects.find(
      (obj) => obj.id === objectManager?.info?.id,
    );
    if (targetObject) {
      dispatch(
        editStorageObject({
          id: objectManager.info.id,
          name: name,
          value: targetObject.value,
          service,
        }),
      );
      return;
    }
    dispatch(
      editStorageObject({
        id: objectManager.info.id,
        name: name,
        value: null,
        service,
      }),
    );
  }, [name, objectManager?.info?.id, objects]);

  const createObject = useCallback(
    (serviceArg: StorageScope) => {
      const withoutCount =
        name !== defaultName && name !== t("common.default_folder_name");
      const type = objectManager?.info?.value
        ? objectManager.info.value
        : "parent";
      if (type === "parent") {
        dispatch(
          addStorageObject({
            service: serviceArg,
            name: name,
            objectType: "parent",
            withoutCount: withoutCount,
          }),
        ).unwrap();
        return;
      } else {
        dispatch(
          addStorageObject({
            service: serviceArg,
            name: name,
            objectType: "child",
            withoutCount: withoutCount,
            currentObjectId: objectManager?.info?.id ?? "",
          }),
        ).unwrap();
      }
    },
    [name, defaultName, setName, objectManager?.info?.value],
  );

  const handleCreateCopyObject = () => {
    if (name === "") {
      return;
    }
    dispatch(
      copyObjectAction({ id: objectManager?.info?.id ?? "", service, name }),
    );
  };

  const handleCloseModal = useCallback(() => {
    closeModal("object_manager", objectManager.subtype);
  }, [[objectManager?.subtype]]);

  const handleOkAction = useCallback(() => {
    const trimmedName = name?.trim() ?? "";

    if (!trimmedName) {
      dispatch(
        createNotification(
          "error",
          "Название объекта не может быть пустой строкой",
        ),
      );
      setName(trimmedName);
      return;
    }
    if (
      objectManager.subtype === "create_object" ||
      objectManager.subtype === "create_folder_object"
    ) {
      addFolder ? addFolder(name) : createObject(service);
    } else if (objectManager.subtype === "edit_object") {
      handleUpdateObjectWithNewName();
    } else if (objectManager.subtype === "create_copy_object") {
      handleCreateCopyObject();
    }
    handleCloseModal();
  }, [name, objectManager?.subtype]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        handleOkAction();
      }
    },
    [name],
  );

  return {
    name,
    title,
    onOkButtonTitle,
    handleOkAction,
    handleCloseModal,
    handleKeyDown,
    handleChangeFolderName,
  };
};
